import { useEffect, useState } from 'react';
import { RETENTION_PERIOD_RADIO_OPTIONS } from '../constants';
import { MergedCustomEvent, RetentionPeriodFormData } from '../types';
import { getRetentionPeriodErrorMessage, convertToDays } from '../utils';

export const initialRetentionPeriodState = {
  input: '30',
  radio: '-1',
  select: 'days',
} as RetentionPeriodFormData;

export const useRetentionPeriodFormData = (events?: MergedCustomEvent[]) => {
  const [formData, setFormDataBase] = useState(initialRetentionPeriodState);
  const { input, radio, select } = formData;
  const [error, setError] = useState('');
  const [retentionPeriod, setRetentionPeriod] = useState(-1);
  const [initialRetentionPeriod, setInitialRetentionPeriod] = useState<number | undefined>();

  const setFormData = (key: string, value: string) => {
    const inputValue = value === '' ? value : parseInt(value, 10).toString();

    setFormDataBase((prev) => ({
      ...prev,
      [key]: key === 'input' ? inputValue : value,
    }));
  };

  useEffect(() => {
    if (events?.length) {
      // find shortest retention period from a list of events and preset formData when updating events
      const sortedEvents = events?.sort((a, b) => (a.retentionPolicy > b.retentionPolicy ? 1 : -1));
      const days = sortedEvents[0].retentionPolicy;
      const radioOption = RETENTION_PERIOD_RADIO_OPTIONS.find(
        (option) => option.value === `${days}`,
      );

      // set initial retention period if there is one for diff checking for disabling buttons
      // should be nested in `if` statement to avoid disabling button based on default value 0
      setInitialRetentionPeriod(days);

      // select available radio button, otherwise set dropdown accordingly with number and duration
      if (radioOption) {
        return setFormData('radio', radioOption.value);
      }

      const isWholeYear = days % 365 === 0;
      const numberOfYears = days / 365;

      return setFormDataBase({
        radio: '999999',
        input: isWholeYear ? `${numberOfYears}` : `${days}`,
        select: isWholeYear ? 'years' : 'days',
      });
    }
  }, []);

  useEffect(() => {
    const radioNumber = parseInt(radio, 10);
    // add radioNumber to find unlimited value in number input fields
    const transformedRetentionPeriod =
      radioNumber === 999999 ? convertToDays(input, select) : radioNumber;
    const errorMessage = getRetentionPeriodErrorMessage(transformedRetentionPeriod, radioNumber);

    setRetentionPeriod(transformedRetentionPeriod);
    setError(errorMessage);
  }, [input, radio, select]);

  return {
    error,
    formData,
    initialRetentionPeriod,
    retentionPeriod,
    setFormData,
  };
};
